import api from "./api";

export type OrderImportOrderType = "carrier" | "self_ship" | "in_store";

// Task 8: hình thức bán của phiên nhập đơn — khớp `fulfillment_type` BE
// (`'INSTANT' | 'PREORDER'`), giao ngay trừ tồn ngay vs đặt trước hẹn ngày giao.
export type OrderImportFulfillmentType = "INSTANT" | "PREORDER";

export interface OrderImportRowNote {
  row: number;
  message: string;
}

// Task 8: bản ghi cấp đơn trong `order_results` của GET /orders/import/:id —
// đúng 9 field BE trả, dùng cho màn kết quả chi tiết (không phải task này).
export interface OrderImportOrderResult {
  order_index: number;
  row: number;
  customer_name: string;
  customer_phone: string;
  item_count: number;
  status: "success" | "failed" | "warning";
  order_id: string | null;
  order_number: string | null;
  message: string;
}

// Task 8: một dòng trong GET /orders/import (MỚI) — màn lịch sử nhập đơn
// (task sau sẽ dùng `orderImportService.list`), đúng 11 field BE trả.
export interface OrderImportListItem {
  import_id: string;
  file_name: string;
  created_at: string;
  created_by_name: string;
  warehouse_name: string;
  order_type: OrderImportOrderType;
  fulfillment_type: OrderImportFulfillmentType;
  status: OrderImportStatus["status"];
  total_orders: number;
  success_orders: number;
  error_count: number;
}

export interface OrderImportStatus {
  import_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  total_rows: number;
  total_orders: number;
  success_orders: number;
  error_rows: OrderImportRowNote[];
  warning_rows: OrderImportRowNote[];
  error_message: string | null;
  // TCR-1529 fix-payment-method-root (Defect 2): BE trả field này ở
  // GET /orders/import/:id (order-import.service.ts, be-tenant) — mã đơn đã
  // tạo, dùng để script bằng chứng Playwright liệt kê đơn cho tester. Type
  // này trước đây thiếu field nên whitelist phía BE (chép nguyên type FE) đã
  // lọc mất nó, làm script bằng chứng đọc `created_order_ids` âm thầm nhận
  // mảng rỗng. Optional vì modal hiện tại chưa hiển thị field này — chỉ khai
  // để khớp đúng hợp đồng response thật.
  created_order_ids?: string[];
  // Task 8: hình thức bán + số ngày giao dự kiến của phiên nhập (PREORDER mới
  // có `delivery_days`, INSTANT trả `null`), cùng tiến độ xử lý cấp đơn.
  fulfillment_type: OrderImportFulfillmentType;
  delivery_days: number | null;
  processed_orders: number;
  order_results: OrderImportOrderResult[];
}

export interface OrderImportStartPayload {
  file: File;
  order_type: OrderImportOrderType;
  warehouse_id: string;
  // Task 8: bắt buộc — server từ chối PREORDER nếu tenant chưa bật
  // `pos_sales_modes` chứa 'PREORDER'.
  fulfillment_type: OrderImportFulfillmentType;
  // Task 8: bắt buộc khi `fulfillment_type === 'PREORDER'`, số nguyên >= 1.
  // Với INSTANT không gửi field này — server bỏ qua nếu client lỡ gửi.
  delivery_days?: number;
}

/** Bóc `filename="..."` khỏi header Content-Disposition; không có thì trả null. */
function parseFileName(disposition: unknown): string | null {
  if (typeof disposition !== "string") return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match ? decodeURIComponent(match[1]) : null;
}

export const orderImportService = {
  downloadTemplate: () =>
    api
      .get<Blob>("/orders/import/template", { responseType: "blob" })
      .then((r) => r.data),

  start: (payload: OrderImportStartPayload) => {
    const form = new FormData();
    form.append("file", payload.file);
    form.append("order_type", payload.order_type);
    form.append("warehouse_id", payload.warehouse_id);
    form.append("fulfillment_type", payload.fulfillment_type);
    if (payload.fulfillment_type === "PREORDER" && payload.delivery_days != null) {
      form.append("delivery_days", String(payload.delivery_days));
    }
    return api
      .post<{ import_id: string }>("/orders/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  getStatus: (importId: string) =>
    api
      .get<OrderImportStatus>(`/orders/import/${importId}`)
      .then((r) => r.data),

  // Task 8: dùng cho màn lịch sử nhập đơn (task sau) — GET /orders/import (MỚI).
  // Task 10 fix round 1: `created_by` là Mongo id (BE `@IsMongoId`) — ô lọc
  // "Người nhập" trên UI là text tự do nên KHÔNG được gửi vào field này (gõ
  // tên sẽ nhận 400). `created_by_name` là field riêng BE thêm, khớp chuỗi
  // con không phân biệt hoa/thường trên `created_by_name` đã lưu của job.
  list: (params: {
    page?: number;
    page_size?: number;
    date_from?: string;
    date_to?: string;
    created_by?: string;
    created_by_name?: string;
    status?: string;
    fulfillment_type?: OrderImportFulfillmentType;
  }) =>
    api
      .get<{
        data: OrderImportListItem[];
        total: number;
        page: number;
        page_size: number;
      }>("/orders/import", { params })
      .then((r) => r.data),

  // Task 8: dùng cho màn lịch sử nhập đơn (task sau) — tải file kết quả xlsx.
  /**
   * Trả kèm tên file do SERVER đặt (`ket-qua-nhap-don-imp_yyyy_MM_dd_hh_mm_ss.xlsx`).
   * FE tự chế tên là sai: server đặt theo thời điểm tạo phiên nhập, thứ mà FE
   * không phải lúc nào cũng có trong tay.
   */
  downloadResultFile: (
    importId: string,
  ): Promise<{ blob: Blob; file_name: string | null }> =>
    api
      .get<Blob>(`/orders/import/${importId}/result-file`, {
        responseType: "blob",
      })
      .then((response) => ({
        blob: response.data,
        file_name: parseFileName(response.headers?.["content-disposition"]),
      })),
};
